import React, { createContext, useContext, useState, useEffect, useRef, } from "react";
import { useGetCompaniesQuery } from "../features/projects/projectsApi";
import { useAuth } from "../auth/useAuth";
import { usePermissions } from "./PermissionContext";
import { useGetCompanyMonthConfigsQuery } from "../features/companyMonthConfigs/companyMonthConfigsApi";
const SELECTED_COMPANIES_KEY = "selectedCompanies";
const CompanyContext = createContext(undefined);
export const CompanyProvider = ({ children, }) => {
    const { account } = useAuth();
    const userEmail = (account === null || account === void 0 ? void 0 : account.username) || "";
    // The user-permissions call is the app-membership gate. Don't fetch companies until it
    // resolves successfully: while it's loading, or if it 403s (a signed-in tenant user who
    // is NOT a provisioned app user), we must not fire by-email — there's nothing to show and
    // it would only produce another 403.
    const { isLoading: isLoadingPermissions, isForbidden: isNotAppUser } = usePermissions();
    const canFetchCompanies = !!userEmail && !isLoadingPermissions && !isNotAppUser;
    const { data: companies, isLoading: isLoadingCompanies, isError: isCompaniesError, error: companiesError, } = useGetCompaniesQuery(userEmail, {
        skip: !canFetchCompanies,
    });
    // 403 here = the caller's role isn't allowed to read entities (or isn't an app user).
    const companiesForbidden = companiesError && "status" in companiesError
        ? companiesError.status === 403
        : false;
    // Initialize from localStorage or empty array
    // Track if we started with an empty selection (no localStorage or empty array)
    const [selectedCompanies, setSelectedCompanies] = useState(() => {
        try {
            const stored = localStorage.getItem(SELECTED_COMPANIES_KEY);
            return stored ? JSON.parse(stored) : [];
        }
        catch (_a) {
            return [];
        }
    });
    const [hasPendingChanges, setHasPendingChanges] = useState(false);
    const hasInitializedRef = useRef(false);
    // Set initial company when data loads (only runs once when companies are first loaded)
    // Always prioritize "Toonen" if available, otherwise select first company
    useEffect(() => {
        if (companies && companies.length > 0 && !hasInitializedRef.current) {
            // Check if "Toonen" is available
            const toonenCompany = companies.find((company) => company.entiteitNaam.toLowerCase() === "toonen");
            // Get current selection from state (don't use selectedCompanies in dependency to avoid re-running)
            let currentSelection = [];
            try {
                currentSelection = JSON.parse(localStorage.getItem(SELECTED_COMPANIES_KEY) || "[]");
            }
            catch (_a) {
                currentSelection = [];
            }
            // Check if current selection is valid (all selected companies still exist)
            const validSelection = currentSelection.filter((selected) => companies.some((c) => c.entiteitNaam === selected));
            // Only auto-select if there's no valid selection from localStorage
            if (validSelection.length === 0) {
                // Always prioritize Toonen if available
                if (toonenCompany) {
                    setSelectedCompanies([toonenCompany.entiteitNaam]);
                    localStorage.setItem(SELECTED_COMPANIES_KEY, JSON.stringify([toonenCompany.entiteitNaam]));
                }
                else {
                    // If Toonen is not available, select first company
                    const defaultCompany = [companies[0].entiteitNaam];
                    setSelectedCompanies(defaultCompany);
                    localStorage.setItem(SELECTED_COMPANIES_KEY, JSON.stringify(defaultCompany));
                }
            }
            else if (validSelection.length !== currentSelection.length) {
                // If some selections are invalid, update to only valid ones
                setSelectedCompanies(validSelection);
                localStorage.setItem(SELECTED_COMPANIES_KEY, JSON.stringify(validSelection));
            }
            // Mark as initialized after first run
            hasInitializedRef.current = true;
        }
    }, [companies]); // Only depend on companies, not selectedCompanies
    // Persist to localStorage whenever selection changes
    useEffect(() => {
        if (selectedCompanies.length > 0) {
            localStorage.setItem(SELECTED_COMPANIES_KEY, JSON.stringify(selectedCompanies));
        }
    }, [selectedCompanies]);
    // Get selected company IDs for backend
    const getSelectedCompanyIds = () => {
        if (!companies)
            return [];
        return companies
            .filter((c) => selectedCompanies.includes(c.entiteitNaam))
            .map((c) => c.entiteitId);
    };
    const selectedCompany = companies && selectedCompanies.length > 0
        ? companies.find((c) => c.entiteitNaam === selectedCompanies[0]) || null
        : null;
    const selectedCompanyId = (selectedCompany === null || selectedCompany === void 0 ? void 0 : selectedCompany.entiteitId) || null;
    const { data: companyMonthConfigs, isLoading: isLoadingCompanyMonthConfigs } = useGetCompanyMonthConfigsQuery(selectedCompanyId || "", {
        skip: !selectedCompanyId,
    });
    // Get selected company names for backend (entiteitNaam)
    const getSelectedCompanyNames = () => {
        return selectedCompanies;
    };
    // "No access" = either no companies assigned, OR a 403 (role not permitted to read
    // entities). We still ignore other errors (e.g. backend down) so a transient outage
    // doesn't look like a permissions problem.
    const hasNoAccess = !isLoadingCompanies &&
        userEmail !== "" &&
        (companiesForbidden ||
            (!isCompaniesError &&
                Array.isArray(companies) &&
                companies.length === 0));
    return (React.createElement(CompanyContext.Provider, { value: {
            selectedCompanies,
            setSelectedCompanies,
            companies,
            isLoadingCompanies,
            getSelectedCompanyIds,
            getSelectedCompanyNames,
            selectedCompany,
            selectedCompanyId,
            companyMonthConfigs,
            isLoadingCompanyMonthConfigs,
            hasPendingChanges,
            setHasPendingChanges,
            hasNoAccess,
        } }, children));
};
export const useCompany = () => {
    const context = useContext(CompanyContext);
    if (context === undefined) {
        throw new Error("useCompany must be used within a CompanyProvider");
    }
    return context;
};
